import { supabase, supabaseRpc } from '@/lib/supabase'
import type {
    UserProfileInsert,
    User
} from '@/models/supabase/User'
import {
    userProfileToUser,
    userProfileToUserWithAuthEmail,
    userToUserProfileUpdate
} from '@/models/supabase/User'
import { authSimpleService } from './auth-simple.service'
import type { Session, User as SupabaseUser } from '@supabase/supabase-js'
import { googleAuthService } from './google-auth.service'
import { appleAuthService } from './apple-auth.service'
import { facebookAuthService } from './facebook-auth.service'
import { detectLocale } from '@/i18n'

export const supabaseAuthService = {
    /**
     * Sign in with Google OAuth
     */
    async signInWithGoogle(): Promise<void> {
        return googleAuthService.signInWithGoogle();
    },

    /**
     * Sign in with Apple OAuth
     */
    async signInWithApple(): Promise<void> {
        return appleAuthService.signInWithApple();
    },

    /**
     * Sign in with Facebook OAuth
     */
    async signInWithFacebook(): Promise<void> {
        return facebookAuthService.signInWithFacebook();
    },

    /**
     * Sign in with email and password
     */
    async signInWithEmailPassword(email: string, password: string): Promise<User> {
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password
        });

        if (authError) throw authError;
        if (!authData.user) throw new Error('Authentication failed');

        // Ensure profile exists
        return authSimpleService.ensureProfileForCurrentUser();
    },

    /**
     * Sign up with email and password
     */
    async signUpWithEmailPassword(email: string, password: string, name?: string): Promise<User> {
        const { data: authData, error: authError } = await supabase.auth.signUp({
            email: email.trim(),
            password,
            options: {
                data: {
                    display_name: name,
                    full_name: name
                }
            }
        });

        if (authError) throw authError;
        if (!authData.user) throw new Error('Sign up failed');

        // Ensure profile exists
        return authSimpleService.ensureProfileForCurrentUser();
    },

    /**
     * Reset password (send recovery email)
     */
    async resetPassword(email: string): Promise<void> {
        const { Capacitor } = await import('@capacitor/core');
        let redirectTo: string;

        if (Capacitor.isNativePlatform()) {
            redirectTo = 'com.withharmonia.app://auth/reset-password';
        } else {
            redirectTo = `${window.location.origin}/auth/reset-password`;
        }

        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
            redirectTo
        });

        if (error) throw error;
    },

    /**
     * Update password for current user (for password reset)
     */
    async updatePassword(newPassword: string): Promise<void> {
        const { error } = await supabase.auth.updateUser({
            password: newPassword
        });
        if (error) throw error;
    },

    /**
     * Update email for current user (for upgrading anonymous accounts)
     */
    async updateEmailForCurrentUser(email: string): Promise<User> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No authenticated user');

        const { error } = await supabase.auth.updateUser({ email });
        if (error) throw error;

        // Ensure profile exists
        return authSimpleService.ensureProfileForCurrentUser();
    },

    /**
     * Complete authentication from session (after magic link callback)
     */
    async completeFromSession(): Promise<User> {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        return authSimpleService.ensureProfileForCurrentUser();
    },

    /**
     * Déconnecte CET appareil, et lui seul.
     *
     * `signOut()` sans argument vaut `scope: 'global'` : il révoque toutes les
     * sessions du compte. Or le téléphone appairé d'un enfant tient une session
     * du parent (#402) : un parent qui se déconnectait de son téléphone
     * déconnectait aussi ses enfants. Un téléphone d'enfant se retire depuis
     * la liste des téléphones appairés, pas par une déconnexion.
     * (Le téléphone retiré passe aussi par ici : sa session est déjà supprimée
     * en base, `local` garantit qu'il ne touche à aucune autre.)
     */
    async logout(): Promise<void> {
        const { error } = await supabase.auth.signOut({ scope: 'local' })
        if (error) throw error
    },

    /**
     * Get current authenticated user data
     */
    async getCurrentUser(): Promise<User | null> {
        // Pas de getUser() ici : ensureProfileForCurrentUser en fait déjà un (il
        // valide le jeton auprès de GoTrue) et rend null sans utilisateur. Le
        // doubler ajoutait un aller-retour réseau qui, tenant le verrou d'auth,
        // retardait toutes les requêtes du démarrage (HARMONIA-76).
        return authSimpleService.getCurrentUserSimple()
    },

    // Removed: sendPasswordReset, updatePassword - passwordless authentication only

    /**
     * Update user profile
     */
    async updateProfile(updates: Partial<User>): Promise<User> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // Si l'email est modifié, mettre à jour l'utilisateur Supabase Auth
        if (updates.email && updates.email !== user.email) {
            // console.log('📧 [Auth Service] Updating email in Supabase Auth:', updates.email);

            const { error: authError } = await supabase.auth.updateUser({
                email: updates.email
            });

            if (authError) {
                console.error('❌ [Auth Service] Error updating email in Supabase Auth:', authError);
                throw new Error(`Failed to update email: ${authError.message}`);
            }

            // console.log('✅ [Auth Service] Email updated in Supabase Auth successfully');
        }

        // Si le nom est modifié, mettre à jour le display_name dans Supabase Auth
        if (updates.name && updates.name !== user.user_metadata?.display_name) {
            // console.log('👤 [Auth Service] Updating display name in Supabase Auth:', updates.name);

            const { error: authError } = await supabase.auth.updateUser({
                data: {
                    display_name: updates.name,
                    full_name: updates.name
                }
            });

            if (authError) {
                console.error('❌ [Auth Service] Error updating display name in Supabase Auth:', authError);
                throw new Error(`Failed to update display name: ${authError.message}`);
            }

            // console.log('✅ [Auth Service] Display name updated in Supabase Auth successfully');
        }

        // Convert User updates to UserProfileUpdate, filtering out invalid fields
        const profileUpdates = userToUserProfileUpdate(updates)

        const { data: updatedProfile, error } = await supabase
            .from('user_profiles')
            .update(profileUpdates)
            .eq('id', user.id)
            .select()
            .single()

        if (error) throw error
        if (!updatedProfile) throw new Error('Failed to update profile')

        // Récupérer l'utilisateur Supabase Auth pour obtenir l'email à jour
        const { data: { user: authUser } } = await supabase.auth.getUser()
        const currentEmail = authUser?.email || ''

        return await userProfileToUserWithAuthEmail(updatedProfile, currentEmail)
    },

    /**
 * Get current Supabase user
 */
    async getCurrentSupabaseUser(): Promise<SupabaseUser | null> {
        const { data: { user } } = await supabase.auth.getUser()
        return user
    },

    /**
     * Get current session
     */
    async getSession(): Promise<Session | null> {
        const { data: { session } } = await supabase.auth.getSession()
        return session
    },

    /**
     * Listen to auth state changes
     * @deprecated Use the auth store directly instead of this service method
     * Auth state changes are now handled centrally in the auth store to avoid conflicts
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onAuthStateChange(callback: (user: User | null) => void) {
        console.warn('[Auth Service] onAuthStateChange is deprecated. Use auth store instead.');
        // Return a dummy subscription to maintain compatibility
        return { data: { subscription: { unsubscribe: () => { } } } };
    },

    /**
     * Check if user is authenticated
     */
    async isAuthenticated(): Promise<boolean> {
        const { data: { session } } = await supabase.auth.getSession()
        return !!session?.user
    },

    /**
     * Create anonymous user account
     */
    async createAnonymousUser(displayName?: string): Promise<User> {
        // Reuse existing anonymous session if available
        let authUserId: string
        const { data: { user: currentUser } } = await supabase.auth.getUser()
        if (currentUser && currentUser.is_anonymous) {
            authUserId = currentUser.id
        } else {
            const { data: authData, error: authError } = await supabase.auth.signInAnonymously()
            if (authError) throw authError
            if (!authData.user) throw new Error('Anonymous user creation failed')
            authUserId = authData.user.id
        }

        const defaultName = displayName || `Utilisateur ${Math.random().toString(36).substr(2, 6)}`

        // Créer directement le profil utilisateur dans la table user_profiles
        const userProfileData: UserProfileInsert = {
            id: authUserId,
            name: defaultName,
            email: '',  // Email vide pour les comptes anonymes
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            // Initialiser les préférences de notification par défaut
            daily_reminder: false,
            point_assignments: false,
            reward_unlocked: false,
            language: detectLocale()
        }

        // Créer ou mettre à jour le profil utilisateur (une seule table)
        const { data: profile, error: insertError } = await supabase
            .from('user_profiles')
            .upsert(userProfileData, { onConflict: 'id' })
            .select()
            .single()

        if (insertError) {
            console.error('Failed to create anonymous user profile:', insertError)
            throw new Error(`Failed to create user profile: ${insertError.message}`)
        }

        if (!profile) {
            throw new Error('Failed to create user profile: No data returned')
        }

        return userProfileToUser(profile)
    },

    /**
     * Check if current user is anonymous
     */
    async isAnonymousUser(): Promise<boolean> {
        const { data: { user } } = await supabase.auth.getUser()
        return !!user?.is_anonymous
    },

    /**
     * Convert anonymous user to permanent account with email (passwordless)
     */
    async upgradeAnonymousUser(email: string, name?: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()

        if (!user?.is_anonymous) {
            throw new Error('User is not anonymous')
        }

        // Update user with email only (no password)
        const { error: updateError } = await supabase.auth.updateUser({
            email,
            data: {
                display_name: name,
                full_name: name
            }
        })

        if (updateError) throw updateError

        // Supabase will automatically send a confirmation email
        // Profile will be updated automatically via onAuthStateChange
    },

    /**
     * Delete anonymous account and all associated data
     */
    async deleteAnonymousAccount(): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()

        if (!user?.is_anonymous) {
            throw new Error('User is not anonymous')
        }

        // Supprimer toutes les données associées via une fonction RPC
        const { error: deleteError } = await supabaseRpc
            .rpc('delete_user_data', { user_id: user.id })

        if (deleteError) {
            console.error('Error deleting user data:', deleteError)
        }

        // Supprimer le compte utilisateur
        const { error: authError } = await supabase.auth.admin.deleteUser(user.id)
        if (authError) throw authError
    }
} 